import { SHOP_CATEGORIES } from './shop';

// ── PRODUCT TAXONOMY ─────────────────────────────────────────────────────────
// What a single listing IS: a type, a subtype under it, and a colour.
//
// THE TYPE LIST IS THE SHOP CATEGORY LIST, reused rather than copied. A shop
// says which categories it sells in; a product says which one it belongs to.
// Two lists would drift the first time a category was added, and search would
// quietly stop matching shops to their own products.
//
// SUBTYPES ARE KEYS, NOT LABELS. What gets written to Firestore and filtered
// against is `tv`, never "Television" — so a name can be corrected or
// translated without migrating a single document. Labels come from i18n as
// `prod_sub_<key>`, the same convention `shop_cat_<key>` already uses.
//
// KEYS ARE UNIQUE ACROSS TYPES, deliberately. `sink` appears under both
// plumbing and kitchen as `plumbing_sink` and `kitchen_sink`, because a search
// that filtered on a bare `sink` would return both and neither would be wrong
// enough to notice. The prefix also keeps the i18n namespace flat.
export const PRODUCT_SUBTYPES = {
  building_materials: [
    'cement', 'plaster_mix', 'insulation', 'timber', 'drywall', 'brick_block',
    'adhesive', 'waterproofing',
  ],
  furniture: [
    'sofa', 'armchair', 'chair', 'table', 'bed', 'wardrobe', 'shelving',
    'desk', 'sideboard', 'stool',
  ],
  openings: [
    'window', 'door_interior', 'door_entrance', 'sliding_door', 'door_handle',
    'window_sill',
  ],
  walls: [
    'paint', 'wallpaper', 'wall_panel', 'decorative_plaster', 'wall_moulding',
  ],
  floor: [
    'parquet', 'laminate', 'floor_tile', 'carpet', 'vinyl', 'skirting',
    'underlay',
  ],
  ceiling: [
    'stretch_ceiling', 'ceiling_panel', 'plasterboard_ceiling', 'cornice',
  ],
  lighting: [
    'chandelier', 'pendant_light', 'ceiling_light', 'wall_light', 'floor_lamp',
    'table_lamp', 'led_strip', 'spotlight', 'outdoor_light',
  ],
  electronics: [
    'tv', 'fridge', 'oven', 'hob', 'extractor_hood', 'dishwasher',
    'washing_machine', 'air_conditioner', 'water_heater_el', 'smart_home',
    'audio',
  ],
  plumbing: [
    'plumbing_sink', 'basin', 'toilet', 'bathtub', 'shower_cabin', 'tap',
    'radiator', 'boiler', 'pipe_fitting',
  ],
  tiles: [
    'ceramic_tile', 'porcelain_tile', 'mosaic', 'natural_stone', 'grout',
  ],
  textiles: [
    'curtains', 'rug', 'cushion', 'bedding', 'upholstery_fabric', 'towels',
  ],
  decor: [
    'mirror', 'wall_art', 'vase', 'clock', 'candle_holder', 'planter_indoor',
    'figurine', 'photo_frame',
  ],
  kitchen: [
    'kitchen_set', 'countertop', 'backsplash', 'kitchen_sink', 'kitchen_tap',
    'cookware', 'kitchen_storage',
  ],
  bathroom: [
    'vanity_unit', 'bathroom_cabinet', 'shower_enclosure', 'towel_rail',
    'bathroom_mirror', 'bathroom_accessory',
  ],
  garden: [
    'garden_furniture', 'planter_outdoor', 'garden_lighting', 'paving',
    'bbq_grill', 'fencing',
  ],
  tools: [
    'power_tool', 'hand_tool', 'ladder', 'measuring_tool', 'fixings',
    'safety_gear',
  ],
};

// The type list, in the order shops already see their categories.
export const PRODUCT_TYPES = SHOP_CATEGORIES;

/** Subtypes under one type. Empty for a type with none — never undefined, so
 *  callers can map over it without a guard. */
export const subtypesOf = (type) => PRODUCT_SUBTYPES[type] || [];

/** Is this pair coherent? Used by validation and by the search filters, which
 *  must drop a subtype the moment the type above it changes. */
export const isSubtypeOf = (type, subtype) => subtypesOf(type).includes(subtype);

// ── COLOUR ───────────────────────────────────────────────────────────────────
// A FIXED PALETTE, not a colour picker. A picker gives every shop a slightly
// different beige and makes "show me the grey sofas" impossible to answer; a
// dozen buckets is what a filter can actually use.
//
// `hex` is only for drawing the swatch — the stored value is always the id, so
// a shade can be adjusted without touching a single listing.
export const PRODUCT_COLORS = [
  { id: 'white',      hex: '#FFFFFF' },
  { id: 'beige',      hex: '#E4D5BC' },
  { id: 'grey',       hex: '#9AA0AE' },
  { id: 'black',      hex: '#1B1C21' },
  { id: 'brown',      hex: '#7A5230' },
  { id: 'wood',       hex: '#C08A4E' },
  { id: 'gold',       hex: '#C9A15A' },
  { id: 'silver',     hex: '#C6CAD1' },
  { id: 'blue',       hex: '#3A5A8C' },
  { id: 'green',      hex: '#4E7A52' },
  { id: 'red',        hex: '#A33A34' },
  { id: 'yellow',     hex: '#D9A62E' },
  { id: 'orange',     hex: '#D2762E' },
  { id: 'pink',       hex: '#D68FA0' },
  { id: 'purple',     hex: '#6B4E8C' },
  // Not a colour but an honest answer for a patterned rug or a mixed set, and
  // the reason the field can be required without being a lie.
  { id: 'multicolor', hex: null },
];

export const PRODUCT_COLOR_IDS = PRODUCT_COLORS.map(c => c.id);
export const colorOf = (id) => PRODUCT_COLORS.find(c => c.id === id) || null;
